import { useState, useRef, useEffect, useCallback } from 'react'
import type { EventConfig, ScreenType, ScreenElement } from '@/types'
import { SCREEN_TYPES } from '@/lib/constants'
import { generateId, uploadFile } from '@/lib/utils'
import {
  Type, Square, Camera, QrCode, ImageIcon, Trash2,
  Move, ChevronUp, ChevronDown, Upload, Eye,
  AlignVerticalJustifyStart, AlignVerticalJustifyCenter, AlignVerticalJustifyEnd,
  AlignHorizontalJustifyStart, AlignHorizontalJustifyCenter, AlignHorizontalJustifyEnd,
  RotateCcw, Monitor, Columns2,
} from 'lucide-react'

const inputCls = 'w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground focus:border-primary/50 transition-colors'

const ELEMENT_TOOLS: { type: ScreenElement['type']; labelKey: string; icon: React.ElementType }[] = [
  { type: 'text', labelKey: 'screen.addText', icon: Type },
  { type: 'image', labelKey: 'screen.addImage', icon: ImageIcon },
  { type: 'shape', labelKey: 'screen.addShape', icon: Square },
  { type: 'camera', labelKey: 'screen.addCamera', icon: Camera },
  { type: 'qr', labelKey: 'screen.addQR', icon: QrCode },
]

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (<div className="space-y-1"><div className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">{label}</div>{children}</div>)
}

import { useI18n } from '@/lib/i18n'

export default function ScreenEditor({ event, onChange }: { event: EventConfig; onChange: (u: Partial<EventConfig>) => void }) {
  const { t } = useI18n()
  const [activeScreen, setActiveScreen] = useState<ScreenType>('welcome')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [dragging, setDragging] = useState<{ id: string; offsetX: number; offsetY: number } | null>(null)
  const [resizing, setResizing] = useState<{ id: string; startW: number; startH: number; mouseX: number; mouseY: number } | null>(null)
  const [rightTab, setRightTab] = useState<'design' | 'settings'>('design')
  const canvasRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)

  const screen = event.screens[activeScreen]
  const useCameraBg = screen.useCameraBg || false

  const updateScreen = useCallback((updates: Partial<typeof screen>) => {
    onChange({ screens: { ...event.screens, [activeScreen]: { ...screen, ...updates } } })
  }, [event.screens, activeScreen, screen, onChange])

  const addElement = (type: ScreenElement['type']) => {
    const el: ScreenElement = {
      id: generateId(), type,
      x: type === 'qr' ? 50 : 660,
      y: type === 'qr' ? ((screen.height || 1080) - 200) : 400,
      width: type === 'text' ? 400 : type === 'qr' ? 150 : 200,
      height: type === 'text' ? 80 : type === 'qr' ? 150 : 200,
      rotation: 0, zIndex: screen.elements.length, opacity: 1,
      content: type === 'text' ? 'New Text' : type === 'qr' ? 'Show Gallery' : undefined,
      color: '#ffffff', fontSize: 48, fontFamily: event.branding.fontFamily, align: 'center',
    }
    updateScreen({ elements: [...screen.elements, el] })
    setSelectedId(el.id)
  }

  const updateElement = useCallback((id: string, updates: Partial<ScreenElement>) => {
    updateScreen({ elements: screen.elements.map((e) => (e.id === id ? { ...e, ...updates } : e)) })
  }, [screen.elements, updateScreen])

  const deleteElement = (id: string) => {
    updateScreen({ elements: screen.elements.filter((e) => e.id !== id) })
    setSelectedId(null)
  }

  const selected = screen.elements.find((e) => e.id === selectedId)

  const canvasW = screen.width || 1920, canvasH = screen.height || 1080
  
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })

  useEffect(() => {
    if (!containerRef.current) return
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry) {
        setContainerSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height
        })
      }
    })
    observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [])

  const scale = Math.max(0.1, Math.min(
    ((containerSize.width || 1000) - 32) / canvasW,
    ((containerSize.height || 800) - 32) / canvasH,
    1
  ))
  const displayW = Math.round(canvasW * scale)
  const displayH = Math.round(canvasH * scale)

  const onMouseDown = (e: React.MouseEvent, el: ScreenElement) => {
    e.stopPropagation(); setSelectedId(el.id)
    const rect = canvasRef.current!.getBoundingClientRect()
    setDragging({ id: el.id, offsetX: (e.clientX - rect.left) / scale - el.x, offsetY: (e.clientY - rect.top) / scale - el.y })
  }

  const onResizeMouseDown = (e: React.MouseEvent, el: ScreenElement) => {
    e.stopPropagation()
    setResizing({ id: el.id, startW: el.width, startH: el.height, mouseX: e.clientX, mouseY: e.clientY })
  }

  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      if (dragging) {
        const rect = canvasRef.current!.getBoundingClientRect()
        updateElement(dragging.id, { x: Math.round((e.clientX - rect.left) / scale - dragging.offsetX), y: Math.round((e.clientY - rect.top) / scale - dragging.offsetY) })
      }
      if (resizing) {
        updateElement(resizing.id, {
          width: Math.max(20, Math.round(resizing.startW + (e.clientX - resizing.mouseX) / scale)),
          height: Math.max(20, Math.round(resizing.startH + (e.clientY - resizing.mouseY) / scale)),
        })
      }
    }
    const handleUp = () => { setDragging(null); setResizing(null) }
    window.addEventListener('mousemove', handleMove)
    window.addEventListener('mouseup', handleUp)
    return () => { window.removeEventListener('mousemove', handleMove); window.removeEventListener('mouseup', handleUp) }
  }, [dragging, resizing, scale, updateElement])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId && !(e.target instanceof HTMLInputElement) && !(e.target instanceof HTMLTextAreaElement)) deleteElement(selectedId)
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [selectedId])

  useEffect(() => {
    if (useCameraBg && activeScreen === 'welcome') {
      navigator.mediaDevices.getUserMedia({ video: true }).then((stream) => { streamRef.current = stream; if (videoRef.current) videoRef.current.srcObject = stream })
    } else { streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null }
    return () => { streamRef.current?.getTracks().forEach((t) => t.stop()) }
  }, [useCameraBg, activeScreen])

  const alignElement = (axis: 'left' | 'centerH' | 'right' | 'top' | 'centerV' | 'bottom') => {
    if (!selected) return
    const map: Record<string, Partial<ScreenElement>> = {
      left: { x: 0 }, centerH: { x: Math.round((canvasW - selected.width) / 2) }, right: { x: canvasW - selected.width },
      top: { y: 0 }, centerV: { y: Math.round((canvasH - selected.height) / 2) }, bottom: { y: canvasH - selected.height },
    }
    updateElement(selected.id, map[axis])
  }

  const sortedElements = [...screen.elements].sort((a, b) => a.zIndex - b.zIndex)

  return (
    <div className="flex h-full flex-col overflow-hidden -m-6">
      {/* TOP — Screen tabs (DSLRBooth style) */}
      <div className="flex items-center justify-center gap-0 border-b border-border bg-card/80 px-4 py-0 shrink-0">
        {SCREEN_TYPES.map((s) => (
          <button key={s} onClick={() => { setActiveScreen(s); setSelectedId(null) }}
            className={`px-6 py-3 text-xs font-bold uppercase tracking-widest transition-colors border-b-2 ${activeScreen === s ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {t(`screen.${s}`)}
          </button>
        ))}
      </div>

      {/* TOOLBAR — Element add buttons */}
      <div className="flex items-center gap-1 border-b border-border bg-card/50 px-4 py-1.5 shrink-0">
        {ELEMENT_TOOLS.map(({ type, labelKey, icon: Icon }) => (
          <button key={type} onClick={() => addElement(type)}
            className="flex flex-col items-center gap-0.5 rounded-md px-3 py-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
            <Icon className="h-4 w-4" />
            <span className="text-[10px]">{t(labelKey)}</span>
          </button>
        ))}
        <div className="mx-2 h-6 w-px bg-border" />
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Monitor className="h-3.5 w-3.5" />
          <span>{canvasW}×{canvasH}</span>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* CENTER — Canvas */}
        <div ref={containerRef} className="flex flex-1 flex-col items-center justify-center overflow-auto bg-[#0a0a10] p-4 gap-2 relative">
          <div className="relative overflow-hidden rounded-lg border border-white/10 shadow-2xl"
            style={{ width: displayW, height: displayH, backgroundColor: screen.backgroundColor }}>
            <div ref={canvasRef} className="absolute inset-0 origin-top-left" style={{ width: canvasW, height: canvasH, transform: `scale(${scale})` }} onClick={() => setSelectedId(null)}>
              {screen.backgroundImage && !useCameraBg && <img src={screen.backgroundImage} className="absolute inset-0 h-full w-full object-cover" alt="" />}
              {useCameraBg && <video ref={videoRef} autoPlay playsInline muted className="absolute inset-0 h-full w-full object-cover" style={{ transform: event.camera.mirrorLiveView ? 'scaleX(-1)' : undefined }} />}
              {screen.startButtonImage?.enabled && screen.startButtonImage.asset && activeScreen === 'welcome' && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ zIndex: 100 }}>
                  <img 
                    src={screen.startButtonImage.asset} 
                    style={{ 
                      width: screen.startButtonImage.width ? `${screen.startButtonImage.width}px` : 'auto', 
                      height: screen.startButtonImage.height ? `${screen.startButtonImage.height}px` : 'auto', 
                      maxWidth: screen.startButtonImage.width ? undefined : '80%',
                      maxHeight: screen.startButtonImage.height ? undefined : '80%',
                      objectFit: 'contain',
                      opacity: screen.startButtonImage.opacity ?? 1.0
                    }} 
                    alt="Start" 
                  />
                </div>
              )}
              {sortedElements.map((el) => (
                <div key={el.id} onMouseDown={(e) => onMouseDown(e, el)} onClick={(e) => e.stopPropagation()}
                  className="absolute group"
                  style={{ left: el.x, top: el.y, width: el.width, height: el.height, transform: `rotate(${el.rotation}deg)`, opacity: el.opacity, zIndex: el.zIndex, cursor: dragging?.id === el.id ? 'grabbing' : 'grab' }}>
                  <div className={`h-full w-full ${selectedId === el.id ? 'ring-2 ring-primary ring-offset-1 ring-offset-transparent' : 'group-hover:ring-1 group-hover:ring-white/30'}`}
                    style={{ backgroundColor: el.type === 'shape' ? (el.backgroundColor || '#3b82f6') : el.backgroundColor, color: el.color, fontSize: el.fontSize || 16, fontFamily: el.fontFamily, borderRadius: el.borderRadius || 0, display: 'flex', alignItems: 'center', justifyContent: el.align === 'center' ? 'center' : el.align === 'right' ? 'flex-end' : 'flex-start', overflow: 'hidden', wordBreak: 'break-word', padding: el.type === 'text' ? '4px 12px' : 0 }}>
                    {el.type === 'text' && el.content}
                    {el.type === 'camera' && <div className="flex h-full w-full items-center justify-center border-2 border-dashed border-white/30"><Camera className="h-8 w-8 text-white/40" /></div>}
                    {el.type === 'qr' && (
                      <div className="flex flex-col h-full w-full items-center justify-center bg-white p-2 text-black rounded-xl shadow-lg border-2 border-white">
                        <div className="flex-1 flex items-center justify-center">
                          <QrCode className="h-10 w-10 text-black" />
                        </div>
                        <span className="text-[10px] font-bold text-center mt-0.5 leading-none text-black">{el.content || 'Show Gallery'}</span>
                      </div>
                    )}
                    {el.type === 'image' && el.src && <img src={el.src} alt="" className="h-full w-full object-cover" />}
                    {el.type === 'image' && !el.src && <div className="flex h-full w-full items-center justify-center border-2 border-dashed border-white/30"><ImageIcon className="h-8 w-8 text-white/40" /></div>}
                  </div>
                  {selectedId === el.id && <div onMouseDown={(e) => onResizeMouseDown(e, el)} className="absolute -bottom-2 -right-2 h-4 w-4 cursor-se-resize rounded-full border-2 border-white bg-primary shadow-lg" />}
                </div>
              ))}
            </div>
            {useCameraBg && <div className="absolute bottom-2 right-3 rounded bg-black/60 px-2 py-0.5 text-[10px] font-bold text-white/60 uppercase tracking-wider">{t('camera.liveView')}</div>}
          </div>
          {/* Bottom bar */}
          <div className="flex items-center gap-4">
            <button onClick={() => selected && updateElement(selected.id, { zIndex: selected.zIndex + 1 })} disabled={!selected}
              className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/5 disabled:opacity-30 transition-colors">
              <ChevronUp className="h-3.5 w-3.5" /> {t('screen.bringForward')}
            </button>
            <button onClick={() => selected && updateElement(selected.id, { zIndex: Math.max(0, selected.zIndex - 1) })} disabled={!selected}
              className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/5 disabled:opacity-30 transition-colors">
              <ChevronDown className="h-3.5 w-3.5" /> {t('screen.sendBackward')}
            </button>
          </div>
        </div>

        {/* RIGHT PANEL — Design / Settings tabs */}
        <div className="flex w-72 shrink-0 flex-col border-l border-border bg-card/50 overflow-auto">
          {/* Tab bar */}
          <div className="flex border-b border-border shrink-0">
            <button onClick={() => setRightTab('design')} className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors border-b-2 ${rightTab === 'design' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
              <Columns2 className="h-3 w-3 inline mr-1" /> {t('screen.design')}
            </button>
            <button onClick={() => setRightTab('settings')} className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors border-b-2 ${rightTab === 'settings' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
              ⚙ {t('screen.settings')}
            </button>
          </div>

          {rightTab === 'design' ? (
            <div className="flex-1 overflow-auto">
              {selected ? (
                <div className="p-3 space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('screen.selected')}</span>
                    <button onClick={() => deleteElement(selected.id)} className="rounded-md p-1 hover:bg-destructive/15 text-muted-foreground hover:text-destructive transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="X"><input type="number" value={Math.round(selected.x)} onChange={(e) => updateElement(selected.id, { x: Number(e.target.value) })} className={inputCls} /></Field>
                    <Field label="Y"><input type="number" value={Math.round(selected.y)} onChange={(e) => updateElement(selected.id, { y: Number(e.target.value) })} className={inputCls} /></Field>
                    <Field label="W"><input type="number" value={selected.width} onChange={(e) => updateElement(selected.id, { width: Number(e.target.value) })} className={inputCls} /></Field>
                    <Field label="H"><input type="number" value={selected.height} onChange={(e) => updateElement(selected.id, { height: Number(e.target.value) })} className={inputCls} /></Field>
                  </div>
                  <div className="flex items-center gap-2">
                    <RotateCcw className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <Field label="Rotate"><div className="flex items-center gap-1"><input type="number" value={selected.rotation} onChange={(e) => updateElement(selected.id, { rotation: Number(e.target.value) })} className={inputCls} /><span className="text-xs text-muted-foreground">°</span></div></Field>
                  </div>
                  <Field label="Opacity"><input type="range" min={0} max={1} step={0.05} value={selected.opacity} onChange={(e) => updateElement(selected.id, { opacity: Number(e.target.value) })} /></Field>
                  <div>
                    <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{t('screen.alignment')}</div>
                    <div className="grid grid-cols-6 gap-1">
                      {([
                        { key: 'left' as const, icon: AlignHorizontalJustifyStart },
                        { key: 'centerH' as const, icon: AlignHorizontalJustifyCenter },
                        { key: 'right' as const, icon: AlignHorizontalJustifyEnd },
                        { key: 'top' as const, icon: AlignVerticalJustifyStart },
                        { key: 'centerV' as const, icon: AlignVerticalJustifyCenter },
                        { key: 'bottom' as const, icon: AlignVerticalJustifyEnd },
                      ]).map(({ key, icon: Icon }) => (
                        <button key={key} onClick={() => alignElement(key)} className="flex h-8 items-center justify-center rounded-md border border-border bg-background text-muted-foreground hover:bg-primary/15 hover:text-primary hover:border-primary/30 transition-colors">
                          <Icon className="h-3.5 w-3.5" />
                        </button>
                      ))}
                    </div>
                  </div>
                  {(selected.type === 'text' || selected.type === 'qr') && (
                    <div className="space-y-2 border-t border-border pt-3">
                      <Field label="Content"><input type="text" value={selected.content || ''} onChange={(e) => updateElement(selected.id, { content: e.target.value })} className={inputCls} /></Field>
                      <div className="grid grid-cols-2 gap-2">
                        <Field label="Font Size"><input type="number" value={selected.fontSize || 32} onChange={(e) => updateElement(selected.id, { fontSize: Number(e.target.value) })} className={inputCls} /></Field>
                        <Field label="Color"><input type="color" value={selected.color || '#ffffff'} onChange={(e) => updateElement(selected.id, { color: e.target.value })} className="h-9 w-full rounded-md border border-input cursor-pointer" /></Field>
                      </div>
                      <Field label="Align"><div className="flex gap-1">{(['left','center','right'] as const).map((a) => (<button key={a} onClick={() => updateElement(selected.id, { align: a })} className={`flex-1 rounded-md border py-1 text-xs capitalize transition-colors ${selected.align === a ? 'border-primary/40 bg-primary/10 text-primary' : 'border-border hover:bg-muted text-muted-foreground'}`}>{a}</button>))}</div></Field>
                      <Field label="Background"><input type="color" value={selected.backgroundColor || '#00000000'} onChange={(e) => updateElement(selected.id, { backgroundColor: e.target.value })} className="h-9 w-full rounded-md border border-input cursor-pointer" /></Field>
                    </div>
                  )}
                  {selected.type === 'shape' && (<div className="space-y-2 border-t border-border pt-3"><Field label="Fill"><input type="color" value={selected.backgroundColor || '#3b82f6'} onChange={(e) => updateElement(selected.id, { backgroundColor: e.target.value })} className="h-9 w-full rounded-md border border-input cursor-pointer" /></Field><Field label="Radius"><input type="number" value={selected.borderRadius || 0} onChange={(e) => updateElement(selected.id, { borderRadius: Number(e.target.value) })} className={inputCls} /></Field></div>)}
                  {selected.type === 'image' && (<div className="space-y-2 border-t border-border pt-3"><Field label={t('screen.imageUrl')}><input type="text" value={selected.src || ''} onChange={(e) => updateElement(selected.id, { src: e.target.value })} className={inputCls} placeholder="https://…" /></Field><label className="flex items-center gap-2 rounded-md border border-dashed border-border bg-background px-3 py-2 cursor-pointer hover:border-primary/40 hover:bg-muted transition-colors"><Upload className="h-3.5 w-3.5 text-muted-foreground" /><span className="text-xs text-muted-foreground">{t('screen.uploadImage')}</span><input type="file" accept="image/*" className="hidden" onChange={async (e) => { const f = e.target.files?.[0]; if (f) { const url = await uploadFile(f); if (url) updateElement(selected.id, { src: url }) } }} /></label></div>)}
                </div>
              ) : (
                <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center"><Move className="h-8 w-8 text-muted-foreground/30" /><p className="text-xs text-muted-foreground">{t('screen.clickToEdit')}</p></div>
              )}
              {/* Layers */}
              <div className="mt-auto border-t border-border p-3 space-y-1">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">{t('screen.layers')}</div>
                {screen.elements.length === 0 && <div className="text-xs text-muted-foreground/50">{t('screen.noElements')}</div>}
                {[...screen.elements].sort((a, b) => b.zIndex - a.zIndex).map((el) => {
                  const tool = ELEMENT_TOOLS.find(t => t.type === el.type)
                  const Icon = tool?.icon ?? Move
                  return (
                    <div key={el.id} onClick={() => setSelectedId(el.id)} className={`flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-xs transition-colors ${selectedId === el.id ? 'bg-primary/15 text-primary' : 'hover:bg-muted text-muted-foreground'}`}>
                      <Eye className="h-3 w-3 shrink-0 opacity-40" />
                      <Icon className="h-3 w-3 shrink-0" />
                      <span className="truncate">{el.content || el.type}</span>
                    </div>
                  )
                })}
              </div>
            </div>
          ) : (
            /* SETTINGS TAB */
            <div className="p-3 space-y-4 overflow-auto">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t(`screen.${activeScreen}`)} {t('screen.settings')}</div>

              {/* Show live view toggle */}
              <div className="flex items-center justify-between rounded-lg border border-border bg-background px-3 py-2.5">
                <div><div className="text-sm font-medium">{t('screen.showLiveView')}</div><div className="text-[11px] text-muted-foreground">{t('screen.showLiveViewDesc')}</div></div>
                <label className="relative inline-flex cursor-pointer"><input type="checkbox" checked={useCameraBg} onChange={(e) => updateScreen({ useCameraBg: e.target.checked })} className="hidden peer" /><div className="w-9 h-5 bg-muted rounded-full peer-checked:bg-primary transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-transform peer-checked:after:translate-x-4" /></label>
              </div>

              {/* Background color */}
              <div className="space-y-1.5">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">{t('screen.backgroundColor')}</div>
                <div className="flex items-center gap-2">
                  <label className="relative cursor-pointer"><div className="h-8 w-8 rounded-lg border border-border" style={{ backgroundColor: screen.backgroundColor }} /><input type="color" value={screen.backgroundColor} onChange={(e) => updateScreen({ backgroundColor: e.target.value })} className="absolute inset-0 opacity-0 w-full h-full cursor-pointer" /></label>
                  <input type="text" value={screen.backgroundColor} onChange={(e) => updateScreen({ backgroundColor: e.target.value })} className="flex-1 rounded-md border border-input bg-background px-2 py-1 text-xs font-mono" />
                </div>
                <label className="flex items-center gap-2 rounded-md border border-dashed border-border bg-background px-2 py-1.5 cursor-pointer hover:border-primary/40 hover:bg-muted transition-colors">
                  <Upload className="h-3 w-3 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">{screen.backgroundImage ? 'Change image' : 'Upload bg image'}</span>
                  <input type="file" accept="image/*" className="hidden" onChange={async (e) => { const f = e.target.files?.[0]; if (f) { const url = await uploadFile(f); if (url) updateScreen({ backgroundImage: url }) } }} />
                </label>
                {screen.backgroundImage && <button onClick={() => updateScreen({ backgroundImage: null })} className="text-xs text-destructive hover:underline">Remove</button>}
              </div>

              {/* Start Button Image */}
              {activeScreen === 'welcome' && (
                <div className="space-y-3 border-t border-border pt-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium">Start Button Image</div>
                      <div className="text-[11px] text-muted-foreground">Show a static image in the center</div>
                    </div>
                    <label className="relative inline-flex cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={screen.startButtonImage?.enabled || false} 
                        onChange={(e) => updateScreen({ startButtonImage: { ...(screen.startButtonImage || { asset: null }), enabled: e.target.checked } })} 
                        className="hidden peer" 
                      />
                      <div className="w-9 h-5 bg-muted rounded-full peer-checked:bg-primary transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-transform peer-checked:after:translate-x-4" />
                    </label>
                  </div>
                  
                  <label className="flex items-center gap-2 rounded-md border border-dashed border-border bg-background px-2 py-1.5 cursor-pointer hover:border-primary/40 hover:bg-muted transition-colors">
                    <Upload className="h-3 w-3 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">{screen.startButtonImage?.asset ? 'Change image' : 'Upload image'}</span>
                    <input 
                      type="file" 
                      accept="image/*" 
                      className="hidden" 
                      onChange={async (e) => { 
                        const f = e.target.files?.[0]; 
                        if (f) { 
                          const url = await uploadFile(f); 
                          if (url) updateScreen({ startButtonImage: { ...(screen.startButtonImage || { enabled: false }), asset: url } }) 
                        } 
                      }} 
                    />
                  </label>
                  {screen.startButtonImage?.asset && (
                    <>
                      <div className="relative w-full h-20 bg-black/20 rounded-md overflow-hidden flex items-center justify-center">
                        <img src={screen.startButtonImage.asset} className="max-h-full max-w-full object-contain" alt="" />
                        <button 
                          onClick={() => updateScreen({ startButtonImage: { ...(screen.startButtonImage || { enabled: false }), asset: null } })} 
                          className="absolute top-1 right-1 text-xs text-destructive hover:underline bg-black/50 px-1 rounded"
                        >
                          Remove
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Field label="Width (px)">
                          <input 
                            type="number" 
                            value={screen.startButtonImage.width || ''} 
                            onChange={(e) => updateScreen({ startButtonImage: { ...screen.startButtonImage!, width: e.target.value ? Number(e.target.value) : undefined } })} 
                            className={inputCls} 
                            placeholder="Auto"
                          />
                        </Field>
                        <Field label="Height (px)">
                          <input 
                            type="number" 
                            value={screen.startButtonImage.height || ''} 
                            onChange={(e) => updateScreen({ startButtonImage: { ...screen.startButtonImage!, height: e.target.value ? Number(e.target.value) : undefined } })} 
                            className={inputCls} 
                            placeholder="Auto"
                          />
                        </Field>
                      </div>
                      <Field label="Opacity">
                        <div className="flex items-center gap-2">
                          <input 
                            type="range" 
                            min="0" 
                            max="1" 
                            step="0.05"
                            value={screen.startButtonImage.opacity ?? 1.0} 
                            onChange={(e) => updateScreen({ startButtonImage: { ...screen.startButtonImage!, opacity: Number(e.target.value) } })} 
                            className="flex-1"
                          />
                          <span className="text-xs font-mono w-8">{Math.round((screen.startButtonImage.opacity ?? 1.0) * 100)}%</span>
                        </div>
                      </Field>
                    </>
                  )}
                </div>
              )}

              {/* Canvas size */}
              <div className="space-y-1.5">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">Canvas Size</div>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Width"><input type="number" value={canvasW} onChange={(e) => updateScreen({ width: Number(e.target.value) })} className={inputCls} /></Field>
                  <Field label="Height"><input type="number" value={canvasH} onChange={(e) => updateScreen({ height: Number(e.target.value) })} className={inputCls} /></Field>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
